import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faLinkSlash, faSpinnerThird, faCheck, faRedo, faHistory } from "@fortawesome/pro-regular-svg-icons";
import { Formik } from "formik";
import axios from "axios";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import NotificationToast from "../../components/Helpers/NotificationToast";
import CustomButton from "../../components/Helpers/CustomButton";
import { getBooking } from "../../store/booking/bookingActions";
import { openModal } from "../../store/modal/modalReducer";
import ErrorBoundary from "../../containers/ErrorBoundary";
import moment from "moment";

const GridBookingMda = ({ current_booking, current_staff }) => {
    const dispatch = useDispatch();
    const [showManualLink, setShowManualLink] = useState(false);
    const [mdaBalance, setMdaBalance] = useState(null);
    const [mdaBalanceLoading, setMdaBalanceLoading] = useState(false);
    const [mdaLoadingT, setMdaLoadingT] = useState(false);
    const [mdaLoadingL, setMdaLoadingL] = useState(false);
    const [bookingCharges, setBookingCharges] = useState([]);
    const [bookingChargesLoading, setBookingChargesLoading] = useState(false);
    const [approvingCharge, setApprovingCharge] = useState(false);
    const [retryingCharge, setRetryingCharge] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [showAllLogs, setShowAllLogs] = useState(false);

    useEffect(() => {
        // Load balance when the component mounts
        if (current_booking?.user_mda?.mda_tenant_id) {
            getBalance(current_booking.id, current_booking?.user.id);
            getBookingCharges(current_booking.id);
        }
    }, [current_booking?.id, current_booking?.user_mda?.mda_tenant_id]);

    useEffect(() => {
        if (mdaBalance?.lease_start_date && mdaBalance?.period_list) {
            const leaseStartDate = new Date(mdaBalance.lease_start_date);
            const leaseYear = leaseStartDate.getFullYear();
            const leaseMonth = leaseStartDate.getMonth() + 1; // JavaScript months are 0-based
            const leasePeriod = parseInt(`${leaseYear}${leaseMonth.toString().padStart(2, "0")}`);

            // Find the closest period to the lease start date
            const periods = [mdaBalance.period_list.FinancialPeriod, mdaBalance.period_list.OwnerStatementPeriod, mdaBalance.period_list.NextTenantStatementPeriod];

            // Find the period that matches or is closest to the lease start date
            const closestPeriod =
                periods.reduce((closest, current) => {
                    if (current >= leasePeriod) {
                        return closest === null ? current : Math.min(closest, current);
                    }
                    return closest;
                }, null) || periods[0]; // Fallback to first period if no future period found

            setSelectedPeriod(closestPeriod);
        }
    }, [mdaBalance]);

    async function getBalance(booking_id, user_id) {
        setMdaBalanceLoading(true);
        setMdaBalance(null);

        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}mda/get_balance`,
            data: { user_id: user_id, booking_id: booking_id },
        })
            .then((response) => {
                setMdaBalance(response.data);
            })
            .catch((response) => {
                toast(<NotificationToast title={"MDA Balance Issue"} message={"Balance could not be loaded"} type="error" />);
            })
            .finally(() => {
                setMdaBalanceLoading(false);
            });
    }

    async function getBookingCharges(booking_id) {
        setBookingChargesLoading(true);

        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}booking_charges/${booking_id}`,
        })
            .then((response) => {
                // Add staff name to each charge that has an approved_by field
                const charges = response.data.map((charge) => {
                    if (charge.approved_by) {
                        // Look up the staff in current_staff if it's the same ID
                        if (charge.approved_by === current_staff.id) {
                            charge.approver_name = `${current_staff.name} ${current_staff.surname || ""}`;
                        }
                    }
                    return charge;
                });
                setBookingCharges(charges);
            })
            .catch((error) => {
                console.error("Error loading booking charges:", error);
            })
            .finally(() => {
                setBookingChargesLoading(false);
            });
    }

    function mda_create_tenant(type, action) {
        if (type === "tenant" && action === "create") {
            setMdaLoadingT(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}mda/create_tenant/${current_booking.user.id}/${current_booking.id}`,
                data: { current_staff: current_staff },
            })
                .then((response) => {
                    let resp = response.data.split("|");

                    if (resp[0] === "updated" || resp[0] === "done") {
                        dispatch(getBooking(current_booking.id));
                        toast(<NotificationToast title={"Tenant Created"} message={"A tenant has been created on MDA"} />);
                    } else {
                        toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />, {
                            autoClose: false,
                        });
                    }
                })
                .catch((response) => {
                    toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />);
                })
                .finally(() => {
                    setMdaLoadingT(false);
                });
        } else if (type === "tenant" && action === "delete") {
            setMdaLoadingT(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}mda/delete_tenant/${current_booking.user.id}/${current_booking.id}`,
                data: { current_staff: current_staff },
            })
                .then((response) => {
                    if (response.data === "done") {
                        dispatch(getBooking(current_booking.id));
                        toast(<NotificationToast title={"Tenant Deleted"} message={"A tenant has been deleted on MDA"} />);
                    } else {
                        toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />, {
                            autoClose: false,
                        });
                    }
                })
                .catch((response) => {
                    toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />);
                })
                .finally(() => {
                    setMdaLoadingT(false);
                });
        } else if (type == "lease" && action == "create") {
            setMdaLoadingL(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}mda/create_lease/${current_booking.user.id}/${current_booking.id}`,
                data: { current_staff: current_staff },
            })
                .then((response) => {
                    if (response.data === "done" || response === "done") {
                        dispatch(getBooking(current_booking.id));
                        toast(<NotificationToast title={"Lease Created"} message={"A lease has been created on MDA"} />);
                    } else {
                        if (response.data === "Can't find Unit!") {
                            dispatch(
                                openModal({
                                    modal_type: "ModalMDAChooseUnit",
                                    modal_props: { current_booking: current_booking },
                                })
                            );
                        } else {
                            toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />, {
                                autoClose: false,
                            });
                        }
                    }
                })
                .catch((response) => {
                    toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />);
                })
                .finally(() => {
                    setMdaLoadingL(false);
                });
        } else if (type == "lease" && action == "delete") {
            setMdaLoadingL(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}mda/delete_lease/${current_booking.user.id}/${current_booking.id}`,
                data: { current_staff: current_staff },
            })
                .then((response) => {
                    if (response.data === "done") {
                        dispatch(getBooking(current_booking.id));
                        toast(<NotificationToast title={"Lease Deleted"} message={"A lease has been deleted on MDA"} />);
                    } else {
                        toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />, {
                            autoClose: false,
                        });
                    }
                })
                .catch((response) => {
                    toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />);
                })
                .finally(() => {
                    setMdaLoadingL(false);
                });
        } else if (type == "lease" && action == "unlink") {
            setMdaLoadingL(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}mda/unlink_lease/${current_booking.user.id}/${current_booking.id}`,
                data: { current_staff: current_staff },
            })
                .then((response) => {
                    dispatch(getBooking(current_booking.id));
                    toast(<NotificationToast title={"Lease Unlinked"} message={"A lease has been unlinked from MDA"} />);
                })
                .catch((response) => {
                    toast(<NotificationToast title={"MDA Issue"} message={response.data} type="error" />);
                })
                .finally(() => {
                    setMdaLoadingL(false);
                });
        }
    }

    async function approveBookingCharge(chargeId) {
        setApprovingCharge(true);

        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}booking_charges/approve/${chargeId}`,
            data: {
                staff_id: current_staff.id,
                booking_id: current_booking.id,
                period: selectedPeriod,
            },
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
        })
            .then((response) => {
                toast(<NotificationToast title={"Charge Approved"} message={"Booking charge has been approved"} />);
                // Refresh booking charges after approval
                getBookingCharges(current_booking.id);
            })
            .catch((error) => {
                toast(<NotificationToast title={"Approval Failed"} message={error.response?.data || "Failed to approve charge"} type="error" />);
                console.error("Error approving booking charge:", error);
            })
            .finally(() => {
                setApprovingCharge(false);
            });
    }

    async function retryBookingCharge(chargeId) {
        setRetryingCharge(true);

        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}booking_charges/${chargeId}/retry`,
            data: {
                staff_id: current_staff.id,
                booking_id: current_booking.id,
                period: selectedPeriod,
            },
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
        })
            .then((response) => {
                toast(<NotificationToast title={"Charge Reset"} message={"Booking charge has been reset for retry"} />);
                // Refresh booking charges after reset
                getBookingCharges(current_booking.id);
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || "Failed to reset charge";
                toast(<NotificationToast title={"Reset Failed"} message={errorMessage} type="error" />);
                console.error("Error resetting booking charge:", error);
            })
            .finally(() => {
                setRetryingCharge(false);
            });
    }

    async function fetchAuditLogs(openModal = true) {
        setLogsLoading(true);
        if (openModal) {
            setShowLogsModal(true);
        }

        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}mda/audit_logs/${current_booking.id}?show_all=${showAllLogs}`,
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
        })
            .then((response) => {
                setAuditLogs(response.data);
            })
            .catch((error) => {
                toast(<NotificationToast title={"Error"} message={"Failed to load audit logs"} type="error" />);
                console.error("Error loading audit logs:", error);
            })
            .finally(() => {
                setLogsLoading(false);
            });
    }

    // Refetch logs when toggle changes
    useEffect(() => {
        if (showLogsModal) {
            fetchAuditLogs(false);
        }
    }, [showAllLogs]);

    // Helper function to get status color based on status code and response
    const getStatusColor = (status, mda_response) => {
        // Check for error in mda_response first, regardless of status
        if (mda_response !== null) {
            try {
                const parsed = JSON.parse(mda_response);
                if (parsed.ErrorCode !== undefined) return "text-red-500"; // Error takes precedence
            } catch (e) {
                // If can't parse, continue with status logic
            }
        }

        // Then check status
        if (status === 1) return "text-green-600";
        if (status === 2) return "text-red-500"; // Failed status
        if (status === 0) {
            if (mda_response === null) return "text-yellow-500"; // Pending
            try {
                const parsed = JSON.parse(mda_response);
                return parsed.ErrorCode === undefined ? "text-green-600" : "text-red-500";
            } catch (e) {
                return "text-red-500"; // Assume failed if can't parse
            }
        }
        return "text-gray-500"; // Unknown
    };

    const getStatusText = (status, mda_response) => {
        // Check for error in mda_response first, regardless of status
        if (mda_response !== null) {
            try {
                const parsed = JSON.parse(mda_response);
                if (parsed.ErrorCode !== undefined) return "Failed";
            } catch (e) {
                // If can't parse, continue with status logic
            }
        }

        // Then check status
        if (status === 1) return "Processed";
        if (status === 2) return "Failed"; // Failed status
        if (status === 0) {
            if (mda_response === null) return "Pending";
            try {
                const parsed = JSON.parse(mda_response);
                return parsed.ErrorCode === undefined ? "Success" : "Failed";
            } catch (e) {
                return "Failed";
            }
        }
        return "Unknown";
    };

    // Helper function to parse error messages
    const parseErrorMessage = (message) => {
        try {
            const response = JSON.parse(message);

            // Check if this is a rejection message
            if (response.status === "rejected") {
                return `Rejected: ${response.reason}`;
            }

            // Check if this is an error message (has ErrorCode)
            if (response.ErrorCode !== undefined) {
                // Get the main error message
                let errorMsg = response.Message || "Unknown error";

                // Add the first detailed error if available
                if (response.FurtherDetails && response.FurtherDetails.length > 0) {
                    errorMsg += `: ${response.FurtherDetails[0].Message}`;
                }

                return errorMsg;
            }

            // Not an error message
            return "Success";
        } catch (e) {
            // If JSON parsing fails, return the raw message
            return message;
        }
    };

    // Helper function to safely format dates - prevents the "Cannot read properties of null" error
    const safeFormatDate = (dateString) => {
        try {
            if (!dateString) return "-";
            return format(new Date(dateString), "dd MMM yyyy HH:mm");
        } catch (error) {
            return "-";
        }
    };

    // Helper function to parse rejection info
    const parseRejectionInfo = (mda_response) => {
        try {
            const response = JSON.parse(mda_response);
            if (response.status === "rejected") {
                return {
                    reason: response.reason,
                    rejected_by: response.rejected_by,
                    rejected_at: response.rejected_at,
                };
            }
            return null;
        } catch (e) {
            return null;
        }
    };

    return (
        <div>
            {/* Show Logs Button - Always visible at top */}
            <div className="mb-4">
                <CustomButton
                    text="Show Logs"
                    styling="w-full"
                    icon={faHistory}
                    onClick={fetchAuditLogs}
                    type="secondary"
                />
            </div>

            {current_booking?.user_mda === undefined || current_booking?.user_mda === null || current_booking?.user_mda?.mda_tenant_id === null || current_booking?.user_mda?.mda_tenant_id === 0 ? (
                <>
                    {!current_staff.building_manager ? (
                        <>
                            <CustomButton
                                text="Create Tenant"
                                styling="w-full"
                                icon={faLink}
                                onClick={() => mda_create_tenant("tenant", "create")}
                                loading={mdaLoadingT ? 1 : 0}
                                loadingtext="Creating Tenant..."
                            />
                            <CustomButton text="Manually Link Tenant" styling="w-full mt-2" icon={faLink} onClick={() => setShowManualLink(true)} type="button" />
                            {showManualLink === true ? (
                                <>
                                    <Formik
                                        initialValues={{ tenant_id: "" }}
                                        validate={(values) => {
                                            const errors = {};
                                            if (!values.tenant_id) {
                                                errors.tenant_id = "Required";
                                            }
                                            return errors;
                                        }}
                                        onSubmit={async (values, { setSubmitting }) => {
                                            values.user_id = current_booking?.user.id;
                                            values.booking_id = current_booking?.id;
                                            await axios({
                                                method: "post",
                                                url: process.env.REACT_APP_API_URL + "mda/manual_mda_link",
                                                data: values,
                                            }).then((response) => {
                                                dispatch(getBooking(current_booking.id));
                                            });
                                        }}
                                    >
                                        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                                            <form onSubmit={handleSubmit} className="mt-2">
                                                <input
                                                    type="text"
                                                    name="tenant_id"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.tenant_id}
                                                    placeholder="Tenant Code"
                                                    className="rounded-lg block w-full font-light border border-gray-300 shadow-sm py-2 pl-2 focus:outline-none focus:ring-primary focus:border-primary text-sm transition ease-linear duration-10 hover:border-primary"
                                                />
                                                {errors.tenant_id && touched.tenant_id && errors.tenant_id}
                                                <br />
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="bg-dark_background hover:bg-dark_background focus:ring-dark_background relative flex text-white uppercase items-center px-4 py-2 rounded-lg text-sm focus:z-10 focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                                >
                                                    Save Tenant Code
                                                </button>
                                            </form>
                                        )}
                                    </Formik>
                                </>
                            ) : (
                                ""
                            )}
                        </>
                    ) : (
                        ""
                    )}
                </>
            ) : (
                <div>
                    <ErrorBoundary>
                        <div>
                            <strong>Tenant ID:</strong>{" "}
                            {current_booking?.user_mda?.mda_tenant_id > 0 ? (
                                <a className="underline text-blue-500" target="_blank" href={"https://managerweb.mdapropsys.com/master-data/tenants/" + current_booking?.user_mda?.mda_tenant_id}>
                                    {current_booking?.user_mda?.mda_tenant_id}
                                </a>
                            ) : (
                                "None"
                            )}
                        </div>

                        {/* Always show balance section with placeholders or loading state */}
                        <div className="mt-2 min-h-36">
                            {mdaBalanceLoading ? (
                                <div className="my-2">
                                    <FontAwesomeIcon icon={faSpinnerThird} className="text-black animate-spin text-xl mr-2" />
                                    <span>Loading balance information...</span>
                                </div>
                            ) : (
                                <>
                                    <strong>Balance {mdaBalance?.date || "-"} :</strong> R {mdaBalance?.balance != null ? mdaBalance.balance.toFixed(2) : "-"}
                                    <br />
                                    <strong>Current Balance: </strong> R {mdaBalance?.current != null ? mdaBalance.current.toFixed(2) : "-"}
                                    <br />
                                    <strong>Lease Start Date: </strong> {mdaBalance?.lease_start_date ? safeFormatDate(mdaBalance.lease_start_date) : "-"}
                                    <br />
                                    <strong>Lease End Date: </strong> {mdaBalance?.lease_end_date ? safeFormatDate(mdaBalance.lease_end_date) : "-"}
                                    <br />
                                    <strong>Current Rental: </strong> R {mdaBalance?.current_rental != null ? mdaBalance.current_rental.toFixed(2) : "-"}
                                    <br />
                                    <strong>Parking: </strong> R {mdaBalance?.parking != null ? mdaBalance.parking.toFixed(2) : "-"}
                                    {mdaBalance?.vacate_date && (
                                        <div className="text-red-500">
                                            <strong>Vacate Date: </strong> {safeFormatDate(mdaBalance.vacate_date)}
                                        </div>
                                    )}
                                    {mdaBalance?.terminate_date && (
                                        <div className="text-red-500">
                                            <strong>Terminate Date: </strong> {safeFormatDate(mdaBalance.terminate_date)}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {!current_staff.building_manager && (process.env.REACT_APP_COLOUR !== "UC" || current_staff.isSuper == 1) ? (
                            <CustomButton
                                text={"Unlink Lease + Tenant from MDA"}
                                styling="w-full mt-2"
                                icon={faLinkSlash}
                                onClick={() => mda_create_tenant("lease", "unlink")}
                                loading={mdaLoadingL ? 1 : 0}
                                loadingtext="Deleting Lease..."
                            />
                        ) : (
                            ""
                        )}

                        {current_booking?.rates &&
                        current_booking?.rates.length > 0 &&
                        current_booking?.rates[current_booking?.rates.length - 1].mda_lease_id &&
                        current_booking?.rates[current_booking?.rates.length - 1].mda_lease_id !== null &&
                        current_booking?.rates[current_booking?.rates.length - 1].mda_lease_id !== 0 ? (
                            <>
                                <div>Lease ID: {current_booking?.rates[current_booking?.rates.length - 1].mda_lease_id}</div>

                                {!current_staff.building_manager && (process.env.REACT_APP_COLOUR !== "UC" || current_staff.isSuper == 1) ? (
                                    <CustomButton
                                        text={"Delete Lease from MDA"}
                                        styling="w-full mt-2"
                                        icon={faLinkSlash}
                                        onClick={() => mda_create_tenant("lease", "delete")}
                                        loading={mdaLoadingL ? 1 : 0}
                                        loadingtext="Deleting Lease..."
                                    />
                                ) : (
                                    ""
                                )}
                            </>
                        ) : (
                            <>
                                {!current_staff.building_manager ? (
                                    <>
                                        <CustomButton
                                            text={"Delete Tenant (Permanent)"}
                                            styling="w-full mt-2"
                                            icon={faLinkSlash}
                                            onClick={() => mda_create_tenant("tenant", "delete")}
                                            loading={mdaLoadingT ? 1 : 0}
                                            loadingtext="Deleting Tenant..."
                                        />

                                        <CustomButton
                                            text="Create Lease (Automatic)"
                                            styling="w-full mt-2"
                                            icon={faLink}
                                            onClick={() => mda_create_tenant("lease", "create")}
                                            loading={mdaLoadingL ? 1 : 0}
                                            loadingtext="Creating Lease..."
                                        />

                                        {(current_staff.id === 1 || current_staff.email === "sarel@qholdings.co.za") && (
                                            <CustomButton
                                                text="Create Lease (Manual)"
                                                styling="w-full mt-2"
                                                icon={faLink}
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            modal_type: "ModalMDAChooseUnit",
                                                            modal_props: { current_booking: current_booking },
                                                        })
                                                    )
                                                }
                                                loading={mdaLoadingL ? 1 : 0}
                                                loadingtext="Creating Lease..."
                                            />
                                        )}
                                    </>
                                ) : (
                                    ""
                                )}
                            </>
                        )}

                        {/* Booking Charges Section */}
                        {mdaBalance?.period_list && (
                            <div className="mt-5">
                                <strong>Period List: </strong>
                                <select className="rounded border border-gray-300 px-2 py-1 text-sm" value={selectedPeriod || ""} onChange={(e) => setSelectedPeriod(e.target.value)}>
                                    <option value={mdaBalance.period_list.FinancialPeriod}>Financial Period: {mdaBalance.period_list.FinancialPeriod}</option>
                                    <option value={mdaBalance.period_list.OwnerStatementPeriod}>Owner Statement Period: {mdaBalance.period_list.OwnerStatementPeriod}</option>
                                    <option value={mdaBalance.period_list.NextTenantStatementPeriod}>Next Tenant Statement Period: {mdaBalance.period_list.NextTenantStatementPeriod}</option>
                                </select>
                            </div>
                        )}
                        <div className="mt-5">
                            <h3 className="font-bold mb-2">Booking Charges</h3>
                            {bookingChargesLoading ? (
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={faSpinnerThird} className="text-black animate-spin text-xl mr-2" />
                                    <span>Loading charges...</span>
                                </div>
                            ) : bookingCharges && bookingCharges.length > 0 ? (
                                <div>
                                    <div className="space-y-4">
                                        {bookingCharges.map((charge, index) => (
                                            <div key={index} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow duration-300">
                                                <div className="px-4 py-5 sm:px-6 bg-gray-50">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h3 className="text-sm font-medium text-gray-900">Period: {charge.period}</h3>
                                                        <div
                                                            className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(charge.status, charge.mda_response)
                                                                .replace("text-", "bg-")
                                                                .replace("500", "100")} ${getStatusColor(charge.status, charge.mda_response)}`}
                                                        >
                                                            {getStatusText(charge.status, charge.mda_response)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mt-1">Batch: {charge.batch_no}</p>
                                                        <p className="text-xs text-gray-500 mt-1">Created: {safeFormatDate(charge.created_at)}</p>
                                                        {charge.approved_by && charge.approved_on && (
                                                            <p className="text-xs text-green-600 mt-1">
                                                                Approved: {safeFormatDate(charge.approved_on)} by{" "}
                                                                {charge.approver_name ||
                                                                    (current_staff.id === charge.approved_by ? `${current_staff.name} ${current_staff.surname || ""}` : charge.approved_by)}
                                                            </p>
                                                        )}
                                                        {charge.mda_response && parseRejectionInfo(charge.mda_response) && (
                                                            <p className="text-xs text-red-600 mt-1">
                                                                Rejected: {safeFormatDate(parseRejectionInfo(charge.mda_response).rejected_at)} by {parseRejectionInfo(charge.mda_response).rejected_by}
                                                                <br />
                                                                Reason: {parseRejectionInfo(charge.mda_response).reason}
                                                            </p>
                                                        )}
                                                        {charge.admin_approved_on && charge.batch_no && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Processed: {safeFormatDate(charge.admin_approved_on)} by{" "}
                                                                {charge.approver_name ||
                                                                    (current_staff.id === charge.approved_by ? `${current_staff.name} ${current_staff.surname || ""}` : charge.approved_by)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="border-t border-gray-200 px-4 py-4">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-sm font-medium text-gray-500">Amount</span>
                                                        <span className="text-sm font-semibold">R {charge.amount ? parseFloat(charge.amount).toFixed(2) : "0.00"}</span>
                                                    </div>

                                                    <p className="text-xs text-gray-600 mt-2">{charge.description}</p>

                                                    {charge.status === 1 && charge.mda_response !== null && parseErrorMessage(charge.mda_response) !== "Success" && (
                                                        <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-500">{parseErrorMessage(charge.mda_response)}</div>
                                                    )}

                                                    {!charge.approved_by && (
                                                        <div className="mt-3 flex justify-end">
                                                            <button
                                                                onClick={() => approveBookingCharge(charge.id)}
                                                                disabled={approvingCharge}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                            >
                                                                {approvingCharge ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-1" />
                                                                        Approving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                                        Approve Charge
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {charge.status === 2 && (
                                                        <div className="mt-3 flex justify-end">
                                                            <button
                                                                onClick={() => retryBookingCharge(charge.id)}
                                                                disabled={retryingCharge}
                                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                            >
                                                                {retryingCharge ? (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-1" />
                                                                        Retrying...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FontAwesomeIcon icon={faRedo} className="mr-1" />
                                                                        Retry Charge
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No charges found</p>
                            )}
                        </div>
                    </ErrorBoundary>
                </div>
            )}

            {/* Audit Logs Modal */}
            {showLogsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowLogsModal(false)}>
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">MDA Audit Logs</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    {showAllLogs ? "Showing all audit records" : "Showing only records with actual value changes"}
                                </p>
                            </div>
                            <button onClick={() => setShowLogsModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
                                ×
                            </button>
                        </div>

                        <div className="mb-4 flex items-center gap-2 p-3 bg-gray-50 rounded">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showAllLogs}
                                    onChange={(e) => setShowAllLogs(e.target.checked)}
                                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm font-medium text-gray-700">Show all audit records (including no-change updates)</span>
                            </label>
                        </div>

                        {logsLoading ? (
                            <div className="text-center py-8">
                                <FontAwesomeIcon icon={faSpinnerThird} className="text-black animate-spin text-xl mr-2" />
                                <span>Loading logs...</span>
                            </div>
                        ) : auditLogs && auditLogs.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="text-left px-4 py-2 border">Date/Time</th>
                                            <th className="text-left px-4 py-2 border">Created By</th>
                                            <th className="text-left px-4 py-2 border">Source</th>
                                            <th className="text-left px-4 py-2 border">Event</th>
                                            <th className="text-left px-4 py-2 border">Changes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditLogs.map((log, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 border whitespace-nowrap">
                                                    {moment(log.created_at).format("DD MMM YYYY HH:mm:ss")}
                                                </td>
                                                <td className="px-4 py-2 border">{log.created_by}</td>
                                                <td className="px-4 py-2 border">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                            log.source === "Scheduled Job"
                                                                ? "bg-blue-100 text-blue-800"
                                                                : log.source === "MDA Tab"
                                                                ? "bg-green-100 text-green-800"
                                                                : "bg-gray-100 text-gray-800"
                                                        }`}
                                                    >
                                                        {log.source}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 border">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                                            log.event === "created"
                                                                ? "bg-green-100 text-green-800"
                                                                : log.event === "updated"
                                                                ? "bg-blue-100 text-blue-800"
                                                                : log.event === "deleted"
                                                                ? "bg-red-100 text-red-800"
                                                                : "bg-gray-100 text-gray-800"
                                                        }`}
                                                    >
                                                        {log.event}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 border text-sm">
                                                    {log.changes && log.changes.length > 0 ? (
                                                        <ul className="list-disc list-inside space-y-1">
                                                            {log.changes.map((change, idx) => (
                                                                <li key={idx}>{change}</li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <span className="text-gray-400">No changes recorded</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">No MDA audit logs found for this booking</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GridBookingMda;
